import { useState, useEffect, useCallback } from 'react';
import {
  FileText, CreditCard, Settings2, Plus, Send, CheckCircle, XCircle,
  RefreshCw, Eye, Download, Wifi, WifiOff, ChevronDown, ChevronUp,
  AlertTriangle, Info, DollarSign, Receipt, Zap, Shield, TrendingUp, Edit2, Save
} from 'lucide-react';
import { smartbillApi, netopiaApi } from '../api/cms';
import type {
  SmartBillSettings, SmartBillInvoice, SmartBillInvoiceItem,
  NetopiaSettings, NetopiaPaymentLog
} from '../types';
import { useLang } from '../context/LangContext';

// ─── Shared UI primitives ────────────────────────────────────────────────────
const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <label className="block text-xs text-white/40 mb-1.5 uppercase tracking-wide font-semibold">{label}</label>
    {children}
  </div>
);

const Input = (props: React.InputHTMLAttributes<HTMLInputElement>) => (
  <input {...props} className={`w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#295CA8] transition-colors ${props.className || ''}`} />
);

const Select = (props: React.SelectHTMLAttributes<HTMLSelectElement> & { children: React.ReactNode }) => (
  <select {...props} className={`w-full bg-[#0a0c12] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#295CA8] transition-colors ${props.className || ''}`} />
);

const Toggle = ({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) => (
  <label className="flex items-center gap-3 cursor-pointer select-none">
    <div onClick={() => onChange(!checked)}
      className={`relative w-10 h-5 rounded-full transition-all ${checked ? 'bg-[#295CA8]' : 'bg-white/10'}`}>
      <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${checked ? 'left-5' : 'left-0.5'}`} />
    </div>
    <span className="text-sm text-white/70">{label}</span>
  </label>
);

const StatusBadge = ({ status }: { status: string }) => {
  const cfg: Record<string, string> = {
    draft: 'bg-white/10 text-white/50', sent: 'bg-blue-500/20 text-blue-400',
    paid: 'bg-green-500/20 text-green-400', cancelled: 'bg-red-500/20 text-red-400',
    pending: 'bg-yellow-500/20 text-yellow-400', authorized: 'bg-blue-500/20 text-blue-400',
    failed: 'bg-red-500/20 text-red-400', refunded: 'bg-purple-500/20 text-purple-400',
  };
  const labels: Record<string, string> = {
    draft: 'Proiect', sent: 'Trimis', paid: 'Plătit', cancelled: 'Anulat',
    pending: 'În așteptare', authorized: 'Autorizat', failed: 'Eșuat', refunded: 'Rambursat',
  };
  return <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${cfg[status] || 'bg-white/10 text-white/50'}`}>{labels[status] || status}</span>;
};

// ─── CONNECTION TEST BANNER ──────────────────────────────────────────────────
const ConnBanner = ({ status, message, onTest, loading }: { status: 'idle' | 'ok' | 'fail'; message: string; onTest: () => void; loading: boolean }) => (
  <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-sm ${status === 'ok' ? 'border-green-500/30 bg-green-500/10 text-green-300' : status === 'fail' ? 'border-red-500/30 bg-red-500/10 text-red-300' : 'border-white/10 bg-white/5 text-white/50'}`}>
    {status === 'ok' ? <Wifi size={16} /> : status === 'fail' ? <WifiOff size={16} /> : <Info size={16} />}
    <span className="flex-1">{status === 'idle' ? 'Salvează credențialele și testează conexiunea.' : message}</span>
    <button onClick={onTest} disabled={loading}
      className="flex items-center gap-1.5 text-xs bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg transition-all disabled:opacity-50">
      <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
      {loading ? 'Se testează...' : 'Testează'}
    </button>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// SMARTBILL TAB
// ═══════════════════════════════════════════════════════════════════════════════
function SmartBillTab() {
  const { t } = useLang();
  const [settings, setSettings] = useState<SmartBillSettings>(smartbillApi.getSettings());
  const [invoices, setInvoices] = useState<SmartBillInvoice[]>(smartbillApi.listInvoices());
  const [saved, setSaved] = useState(false);
  const [connStatus, setConnStatus] = useState<'idle' | 'ok' | 'fail'>('idle');
  const [connMsg, setConnMsg] = useState('');
  const [connLoading, setConnLoading] = useState(false);
  const [showNewInvoice, setShowNewInvoice] = useState(false);
  const [activeSection, setActiveSection] = useState<'settings' | 'invoices' | 'new'>('settings');

  // New invoice form state
  const emptyItem = (): SmartBillInvoiceItem => ({
    name: '', measuringUnitName: 'serviciu', quantity: 1, price: 0,
    currency: settings.currency, isTaxIncluded: false,
    taxName: settings.taxName, taxPercentage: settings.taxPercentage, isService: true,
  });
  const [newInv, setNewInv] = useState({
    clientName: '', clientCui: '', clientEmail: '', clientAddress: '', clientCity: 'București', clientCounty: 'Ilfov',
    currency: settings.currency, items: [emptyItem()], orderId: '',
  });
  const [creating, setCreating] = useState(false);
  const [sendingEmail, setSendingEmail] = useState<string | null>(null);

  const setSetting = <K extends keyof SmartBillSettings>(k: K, v: SmartBillSettings[K]) =>
    setSettings(s => ({ ...s, [k]: v }));

  const saveSettings = () => {
    smartbillApi.saveSettings(settings);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const testConn = async () => {
    setConnLoading(true);
    smartbillApi.saveSettings(settings);
    const res = await smartbillApi.testConnection();
    setConnStatus(res.ok ? 'ok' : 'fail');
    setConnMsg(res.message || (res.ok ? 'Conexiune reușită!' : 'Conexiune eșuată.'));
    setConnLoading(false);
  };

  const createInvoice = async () => {
    setCreating(true);
    const total = newInv.items.reduce((s, i) => s + i.quantity * i.price, 0);
    try {
      const inv = await smartbillApi.createInvoice({ ...newInv, total, status: settings.isDraft ? 'draft' : 'sent' });
      setInvoices(smartbillApi.listInvoices());
      setActiveSection('invoices');
      setNewInv({ clientName: '', clientCui: '', clientEmail: '', clientAddress: '', clientCity: 'București', clientCounty: 'Ilfov', currency: settings.currency, items: [emptyItem()], orderId: '' });
    } finally { setCreating(false); }
  };

  const sendEmail = async (id: string) => {
    setSendingEmail(id);
    await smartbillApi.sendInvoiceEmail(id);
    setInvoices(prev => prev.map(i => i.id === id ? { ...i, status: 'sent' } : i));
    smartbillApi.saveInvoices(invoices.map(i => i.id === id ? { ...i, status: 'sent' } : i));
    setSendingEmail(null);
  };

  const updateItem = (idx: number, k: keyof SmartBillInvoiceItem, v: unknown) =>
    setNewInv(f => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, [k]: v } : it) }));

  const sections = [
    { id: 'settings', label: t('Configurare', 'Configuration'), icon: Settings2 },
    { id: 'invoices', label: t('Facturi', 'Invoices'), icon: Receipt },
    { id: 'new', label: t('Factură nouă', 'New Invoice'), icon: Plus },
  ] as const;

  return (
    <div className="space-y-6">
      {/* Sub-nav */}
      <div className="flex gap-1 bg-white/5 border border-white/10 rounded-xl p-1 w-fit">
        {sections.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setActiveSection(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeSection === id ? 'bg-[#295CA8] text-white' : 'text-white/40 hover:text-white'}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {/* ── SETTINGS ── */}
      {activeSection === 'settings' && (
        <div className="space-y-5">
          <ConnBanner status={connStatus} message={connMsg} onTest={testConn} loading={connLoading} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Auth */}
            <div className="bg-[#07090f] border border-white/10 rounded-xl p-5 space-y-4">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2"><Shield size={15} className="text-[#4ab8ff]" /> {t('Autentificare API', 'API Authentication')}</h3>
              <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg text-xs text-blue-300 leading-relaxed">
                💡 Găsești credențialele în <strong>SmartBill Cloud → Contul Meu → Integrari → Acces API</strong>
              </div>
              <Field label="Email cont SmartBill">
                <Input type="email" value={settings.username} onChange={e => setSetting('username', e.target.value)} placeholder="office@firma.ro" />
              </Field>
              <Field label="Token API">
                <Input type="password" value={settings.token} onChange={e => setSetting('token', e.target.value)} placeholder="••••••••••••••••••••••••" />
              </Field>
              <Field label="CIF firmă emitentă">
                <Input value={settings.companyVatCode} onChange={e => setSetting('companyVatCode', e.target.value)} placeholder="RO34799221" />
              </Field>
            </div>

            {/* Invoice defaults */}
            <div className="bg-[#07090f] border border-white/10 rounded-xl p-5 space-y-4">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2"><FileText size={15} className="text-[#4ab8ff]" /> {t('Setări factură', 'Invoice Defaults')}</h3>
              <Field label="Serie factură">
                <Input value={settings.seriesName} onChange={e => setSetting('seriesName', e.target.value)} placeholder="FACT" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Monedă implicită">
                  <Select value={settings.currency} onChange={e => setSetting('currency', e.target.value as 'RON' | 'EUR')}>
                    <option value="RON">RON</option>
                    <option value="EUR">EUR</option>
                  </Select>
                </Field>
                <Field label="TVA %">
                  <Input type="number" value={settings.taxPercentage} onChange={e => setSetting('taxPercentage', +e.target.value)} placeholder="19" />
                </Field>
              </div>
              <Field label="Denumire taxă">
                <Input value={settings.taxName} onChange={e => setSetting('taxName', e.target.value)} placeholder="TVA" />
              </Field>
              <Field label="Emis de (nume persoană)">
                <Input value={settings.issuerName} onChange={e => setSetting('issuerName', e.target.value)} placeholder="INTERPERSONAL S.R.L." />
              </Field>
            </div>
          </div>

          {/* Behavior toggles */}
          <div className="bg-[#07090f] border border-white/10 rounded-xl p-5 space-y-4">
            <h3 className="text-sm font-semibold text-white flex items-center gap-2"><Zap size={15} className="text-[#4ab8ff]" /> {t('Comportament automat', 'Automatic behavior')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Toggle checked={settings.sendEmailOnCreate} onChange={v => setSetting('sendEmailOnCreate', v)}
                label={t('Trimite email automat la creare factură', 'Auto-send email on invoice creation')} />
              <Toggle checked={settings.isDraft} onChange={v => setSetting('isDraft', v)}
                label={t('Emite ca proformă (nu factură fiscală)', 'Issue as proforma (not fiscal invoice)')} />
            </div>
            <div className="text-xs text-white/30 leading-relaxed mt-1">
              ℹ️ {t('Când un client finalizează plata online, sistemul va crea automat factura în SmartBill și o va trimite pe email dacă opțiunea este activă.', 'When a client completes online payment, the system will automatically create the invoice in SmartBill and email it if the option is active.')}
            </div>
          </div>

          {/* Workflow diagram */}
          <div className="bg-[#07090f] border border-white/10 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-white mb-4">{t('Flux automat plată → factură', 'Automatic payment → invoice flow')}</h3>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              {[
                { icon: '🛒', label: t('Client alege serviciu', 'Client selects service') },
                { icon: '💳', label: t('Plată Netopia', 'Netopia payment') },
                { icon: '✅', label: t('Confirmare IPN', 'IPN confirmation') },
                { icon: '📄', label: t('Factură SmartBill', 'SmartBill invoice') },
                { icon: '📧', label: t('Email client', 'Client email') },
              ].map((step, i, arr) => (
                <>
                  <div key={i} className="flex flex-col items-center gap-1 bg-white/5 rounded-lg px-3 py-2 min-w-[90px] text-center">
                    <span className="text-base">{step.icon}</span>
                    <span className="text-white/60 leading-tight">{step.label}</span>
                  </div>
                  {i < arr.length - 1 && <span key={`a${i}`} className="text-white/20 text-base">→</span>}
                </>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button onClick={saveSettings}
              className="inline-flex items-center gap-2 bg-[#295CA8] hover:bg-[#3D72B8] text-white px-6 py-2.5 rounded-lg text-sm font-medium transition-colors">
              {saved ? <><CheckCircle size={16} /> {t('Salvat!', 'Saved!')}</> : t('Salvează configurarea', 'Save configuration')}
            </button>
            <span className="text-xs text-white/30">{t('Credențialele sunt stocate securizat pe server.', 'Credentials are stored securely on the server.')}</span>
          </div>
        </div>
      )}

      {/* ── INVOICES LIST ── */}
      {activeSection === 'invoices' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-white/40">{invoices.length} {t('facturi înregistrate', 'invoices registered')}</div>
            <button onClick={() => setActiveSection('new')}
              className="inline-flex items-center gap-2 bg-[#295CA8] hover:bg-[#3D72B8] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
              <Plus size={15} /> {t('Factură nouă', 'New Invoice')}
            </button>
          </div>

          {invoices.length === 0 ? (
            <div className="py-16 text-center border border-dashed border-white/10 rounded-xl">
              <FileText size={32} className="mx-auto mb-3 text-white/20" />
              <p className="text-white/30 text-sm">{t('Nicio factură emisă încă.', 'No invoices issued yet.')}</p>
              <button onClick={() => setActiveSection('new')} className="mt-4 text-[#4ab8ff] text-sm hover:underline">{t('Creează prima factură', 'Create first invoice')}</button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    {[t('Nr.', 'Nr.'), t('Client', 'Client'), t('Email', 'Email'), t('Total', 'Total'), 'Status', t('Data', 'Date'), t('Acțiuni', 'Actions')].map(h => (
                      <th key={h} className="text-left px-3 py-2.5 text-xs text-white/30 uppercase tracking-wide font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {invoices.map(inv => (
                    <tr key={inv.id} className="hover:bg-white/3 transition-colors group">
                      <td className="px-3 py-3 font-mono text-[#4ab8ff] text-xs">{inv.number || inv.id.slice(-6).toUpperCase()}</td>
                      <td className="px-3 py-3 text-white font-medium">{inv.clientName}<div className="text-xs text-white/30">{inv.clientCui}</div></td>
                      <td className="px-3 py-3 text-white/50 text-xs">{inv.clientEmail}</td>
                      <td className="px-3 py-3 font-semibold text-white">{inv.total.toLocaleString()} {inv.currency}</td>
                      <td className="px-3 py-3"><StatusBadge status={inv.status} /></td>
                      <td className="px-3 py-3 text-white/40 text-xs">{inv.createdAt}</td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          {inv.status === 'draft' && (
                            <button onClick={() => sendEmail(inv.id)} disabled={sendingEmail === inv.id}
                              title={t('Trimite email', 'Send email')}
                              className="p-1.5 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-all disabled:opacity-50">
                              {sendingEmail === inv.id ? <RefreshCw size={13} className="animate-spin" /> : <Send size={13} />}
                            </button>
                          )}
                          <button title={t('Descarcă PDF', 'Download PDF')}
                            className="p-1.5 rounded-lg bg-white/10 text-white/50 hover:bg-white/20 transition-all">
                            <Download size={13} />
                          </button>
                          <button title="Preview"
                            className="p-1.5 rounded-lg bg-white/10 text-white/50 hover:bg-white/20 transition-all">
                            <Eye size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── NEW INVOICE ── */}
      {activeSection === 'new' && (
        <div className="space-y-5 max-w-3xl">
          <div className="bg-[#07090f] border border-white/10 rounded-xl p-5 space-y-4">
            <h3 className="text-sm font-semibold text-white">{t('Date client', 'Client details')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label={t('Nume client / Companie', 'Client name / Company')}>
                <Input value={newInv.clientName} onChange={e => setNewInv(f => ({ ...f, clientName: e.target.value }))} placeholder="TechSoft SRL" />
              </Field>
              <Field label="CUI / CIF">
                <Input value={newInv.clientCui} onChange={e => setNewInv(f => ({ ...f, clientCui: e.target.value }))} placeholder="RO12345678" />
              </Field>
              <Field label="Email">
                <Input type="email" value={newInv.clientEmail} onChange={e => setNewInv(f => ({ ...f, clientEmail: e.target.value }))} placeholder="client@firma.ro" />
              </Field>
              <Field label={t('Adresă', 'Address')}>
                <Input value={newInv.clientAddress} onChange={e => setNewInv(f => ({ ...f, clientAddress: e.target.value }))} placeholder="Str. Exemplu nr. 1" />
              </Field>
              <Field label={t('Oraș', 'City')}>
                <Input value={newInv.clientCity} onChange={e => setNewInv(f => ({ ...f, clientCity: e.target.value }))} placeholder="București" />
              </Field>
              <Field label={t('Județ', 'County')}>
                <Input value={newInv.clientCounty} onChange={e => setNewInv(f => ({ ...f, clientCounty: e.target.value }))} placeholder="Ilfov" />
              </Field>
              <Field label={t('Nr. comandă asociat', 'Associated order #')}>
                <Input value={newInv.orderId} onChange={e => setNewInv(f => ({ ...f, orderId: e.target.value }))} placeholder="CMD-2026-001" />
              </Field>
              <Field label={t('Monedă', 'Currency')}>
                <Select value={newInv.currency} onChange={e => setNewInv(f => ({ ...f, currency: e.target.value as 'RON' | 'EUR' }))}>
                  <option value="RON">RON</option>
                  <option value="EUR">EUR</option>
                </Select>
              </Field>
            </div>
          </div>

          {/* Line items */}
          <div className="bg-[#07090f] border border-white/10 rounded-xl p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-white">{t('Articole factură', 'Invoice items')}</h3>
              <button onClick={() => setNewInv(f => ({ ...f, items: [...f.items, emptyItem()] }))}
                className="inline-flex items-center gap-1.5 text-xs bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 px-3 py-1.5 rounded-lg transition-all">
                <Plus size={13} /> {t('Adaugă articol', 'Add item')}
              </button>
            </div>

            <div className="space-y-3">
              {newInv.items.map((item, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-end p-3 bg-white/3 rounded-lg border border-white/5">
                  <div className="col-span-4">
                    {idx === 0 && <div className="text-xs text-white/30 mb-1">{t('Denumire', 'Name')}</div>}
                    <Input value={item.name} onChange={e => updateItem(idx, 'name', e.target.value)} placeholder="Site Business" />
                  </div>
                  <div className="col-span-2">
                    {idx === 0 && <div className="text-xs text-white/30 mb-1">U.M.</div>}
                    <Select value={item.measuringUnitName} onChange={e => updateItem(idx, 'measuringUnitName', e.target.value)}>
                      <option value="serviciu">serviciu</option>
                      <option value="buc">buc</option>
                      <option value="luna">luna</option>
                      <option value="an">an</option>
                      <option value="ora">ora</option>
                    </Select>
                  </div>
                  <div className="col-span-1">
                    {idx === 0 && <div className="text-xs text-white/30 mb-1">Qty</div>}
                    <Input type="number" value={item.quantity} onChange={e => updateItem(idx, 'quantity', +e.target.value)} placeholder="1" />
                  </div>
                  <div className="col-span-2">
                    {idx === 0 && <div className="text-xs text-white/30 mb-1">{t('Preț', 'Price')}</div>}
                    <Input type="number" value={item.price} onChange={e => updateItem(idx, 'price', +e.target.value)} placeholder="0.00" />
                  </div>
                  <div className="col-span-2">
                    {idx === 0 && <div className="text-xs text-white/30 mb-1">TVA %</div>}
                    <Input type="number" value={item.taxPercentage} onChange={e => updateItem(idx, 'taxPercentage', +e.target.value)} placeholder="19" />
                  </div>
                  <div className="col-span-1 flex justify-end">
                    {newInv.items.length > 1 && (
                      <button onClick={() => setNewInv(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }))}
                        className="p-2 rounded-lg text-white/20 hover:text-red-400 hover:bg-red-500/10 transition-all">
                        <XCircle size={15} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Total */}
            <div className="flex justify-end pt-2 border-t border-white/10">
              <div className="text-right">
                <div className="text-xs text-white/30 mb-0.5">{t('Total fără TVA', 'Total without VAT')}</div>
                <div className="text-xl font-bold text-white">
                  {newInv.items.reduce((s, i) => s + i.quantity * i.price, 0).toLocaleString()} {newInv.currency}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button onClick={createInvoice} disabled={creating || !newInv.clientName || !newInv.clientEmail}
              className="inline-flex items-center gap-2 bg-[#295CA8] hover:bg-[#3D72B8] text-white px-6 py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-40">
              {creating ? <RefreshCw size={16} className="animate-spin" /> : <FileText size={16} />}
              {creating ? t('Se emite...', 'Issuing...') : (settings.isDraft ? t('Salvează proformă', 'Save as proforma') : t('Emite factură', 'Issue invoice'))}
            </button>
            {settings.sendEmailOnCreate && !settings.isDraft && (
              <span className="text-xs text-[#4ab8ff] flex items-center gap-1.5"><Send size={12} /> {t('Email automat activat', 'Auto email enabled')}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// NETOPIA TAB
// ═══════════════════════════════════════════════════════════════════════════════
function NetopiaTab() {
  const { t } = useLang();
  const [settings, setSettings] = useState<NetopiaSettings>(netopiaApi.getSettings());
  const [payments, setPayments] = useState<NetopiaPaymentLog[]>(netopiaApi.listPayments());
  const [saved, setSaved] = useState(false);
  const [connStatus, setConnStatus] = useState<'idle' | 'ok' | 'fail'>('idle');
  const [connMsg, setConnMsg] = useState('');
  const [connLoading, setConnLoading] = useState(false);
  const [section, setSection] = useState<'settings' | 'payments' | 'test'>('settings');
  const [testAmount, setTestAmount] = useState('10.00');
  const [testEmail, setTestEmail] = useState('test@firma.ro');
  const [testRunning, setTestRunning] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; url?: string; message?: string } | null>(null);
  const [showIpnCode, setShowIpnCode] = useState(false);

  const set = <K extends keyof NetopiaSettings>(k: K, v: NetopiaSettings[K]) =>
    setSettings(s => ({ ...s, [k]: v }));

  const saveSettings = () => { netopiaApi.saveSettings(settings); setSaved(true); setTimeout(() => setSaved(false), 2000); };

  const testConn = async () => {
    setConnLoading(true);
    netopiaApi.saveSettings(settings);
    const res = await netopiaApi.testConnection();
    setConnStatus(res.ok ? 'ok' : 'fail');
    setConnMsg(res.message || (res.ok ? 'Conexiune reușită!' : 'Conexiune eșuată. Verifică credențialele.'));
    setConnLoading(false);
  };

  const runTestPayment = async () => {
    setTestRunning(true);
    setTestResult(null);
    try {
      const res = await netopiaApi.initiatePayment(
        `TEST-${Date.now()}`, parseFloat(testAmount), testEmail, 'Client Test', 'Plată test Interpersonal'
      );
      setTestResult({ ok: true, url: res.redirectUrl, message: 'Plată inițiată cu succes. Redirect URL generat.' });
    } catch (e) {
      setTestResult({ ok: false, message: String(e) });
    }
    setTestRunning(false);
  };

  const ipnCode = `<?php
// netopia-ipn.php — Plasează-l la: ${settings.notifyUrl}
require_once '../cms-api/_common.php';
$payload = json_decode(file_get_contents('php://input'), true);
$ntpId = $payload['payment']['ntpID'] ?? '';
$status = $payload['payment']['status'] ?? 0;
$orderId = $payload['order']['orderID'] ?? '';
// status 3 = paid, 5 = confirmed
if (in_array($status, [3, 5])) {
    // 1. Actualizează comanda
    // 2. Declanșează SmartBill -> creare factură
    // 3. Trimite email confirmare cu PDF factură
    triggerAutoInvoice($orderId);
}
http_response_code(200);
echo json_encode(['status' => 'received']);`;

  const sections = [
    { id: 'settings', label: t('Configurare', 'Configuration'), icon: Settings2 },
    { id: 'payments', label: t('Plăți', 'Payments'), icon: DollarSign },
    { id: 'test', label: t('Testare', 'Testing'), icon: Zap },
  ] as const;

  return (
    <div className="space-y-6">
      {/* Sub-nav */}
      <div className="flex gap-1 bg-white/5 border border-white/10 rounded-xl p-1 w-fit">
        {sections.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setSection(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${section === id ? 'bg-[#295CA8] text-white' : 'text-white/40 hover:text-white'}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {/* ── SETTINGS ── */}
      {section === 'settings' && (
        <div className="space-y-5">
          <ConnBanner status={connStatus} message={connMsg} onTest={testConn} loading={connLoading} />

          {/* Live/Sandbox toggle */}
          <div className={`flex items-center gap-4 px-5 py-4 rounded-xl border ${settings.isLive ? 'border-green-500/30 bg-green-500/10' : 'border-yellow-500/30 bg-yellow-500/10'}`}>
            <div className={`w-3 h-3 rounded-full ${settings.isLive ? 'bg-green-400 shadow-[0_0_8px_#4ade80]' : 'bg-yellow-400 shadow-[0_0_8px_#facc15]'}`} />
            <div className="flex-1">
              <div className="text-sm font-semibold text-white">{settings.isLive ? '🟢 Producție (LIVE)' : '🟡 Sandbox (Testare)'}</div>
              <div className="text-xs text-white/40 mt-0.5">{settings.isLive ? 'Plățile reale sunt procesate.' : 'Plățile de test nu sunt reale.'}</div>
            </div>
            <Toggle checked={settings.isLive} onChange={v => set('isLive', v)} label={t('Activează producție', 'Enable production')} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Auth */}
            <div className="bg-[#07090f] border border-white/10 rounded-xl p-5 space-y-4">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2"><Shield size={15} className="text-[#4ab8ff]" /> {t('Credențiale Netopia', 'Netopia Credentials')}</h3>
              <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg text-xs text-blue-300 leading-relaxed">
                💡 Găsești credențialele în <strong>Netopia Admin Panel → Profil → Securitate</strong>. Activează mai întâi mediul de testare dacă ești în Sandbox.
              </div>
              <Field label="POS Signature (Merchant ID)">
                <Input value={settings.posSignature} onChange={e => set('posSignature', e.target.value)} placeholder="XXXX-XXXX-XXXX-XXXX-XXXX" />
              </Field>
              <Field label="API Key">
                <Input type="password" value={settings.apiKey} onChange={e => set('apiKey', e.target.value)} placeholder="••••••••••••••••••••••••••••••••" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t('Monedă', 'Currency')}>
                  <Select value={settings.currency} onChange={e => set('currency', e.target.value as 'RON' | 'EUR')}>
                    <option value="RON">RON</option>
                    <option value="EUR">EUR</option>
                  </Select>
                </Field>
                <Field label={t('Limbă', 'Language')}>
                  <Select value={settings.language} onChange={e => set('language', e.target.value as 'ro' | 'en')}>
                    <option value="ro">Română</option>
                    <option value="en">English</option>
                  </Select>
                </Field>
              </div>
            </div>

            {/* URLs */}
            <div className="bg-[#07090f] border border-white/10 rounded-xl p-5 space-y-4">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2"><Wifi size={15} className="text-[#4ab8ff]" /> {t('URL-uri webhook', 'Webhook URLs')}</h3>
              <Field label="Notify URL (IPN)">
                <Input value={settings.notifyUrl} onChange={e => set('notifyUrl', e.target.value)} placeholder="https://site.ro/cms-api/netopia-ipn.php" />
              </Field>
              <Field label="Redirect URL">
                <Input value={settings.redirectUrl} onChange={e => set('redirectUrl', e.target.value)} placeholder="https://site.ro/multumim" />
              </Field>

              <div className="p-3 bg-white/5 rounded-lg border border-white/10 text-xs text-white/40 space-y-1">
                <div className="text-white/60 font-semibold mb-1">{t('URL-uri Netopia:', 'Netopia endpoints:')}</div>
                <div><span className="text-yellow-400">Sandbox:</span> secure.sandbox.netopia-payments.com</div>
                <div><span className="text-green-400">Live:</span> secure.mobilpay.ro</div>
              </div>

              {/* IPN code snippet */}
              <button onClick={() => setShowIpnCode(v => !v)}
                className="flex items-center gap-2 text-xs text-[#4ab8ff] hover:text-white transition-colors">
                {showIpnCode ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                {t('Arată codul IPN handler (PHP)', 'Show IPN handler code (PHP)')}
              </button>
              {showIpnCode && (
                <pre className="bg-[#0a0c12] border border-white/10 rounded-lg p-3 text-xs text-green-300 overflow-x-auto whitespace-pre-wrap leading-relaxed">
                  {ipnCode}
                </pre>
              )}
            </div>
          </div>

          {/* Test card info */}
          <div className="bg-[#07090f] border border-white/10 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2"><CreditCard size={15} className="text-[#4ab8ff]" /> {t('Carduri de test Netopia', 'Netopia test cards')}</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b border-white/10">
                  {[t('Nr. card', 'Card No.'), t('Expirare', 'Expiry'), 'CVV', 'Rezultat'].map(h => (
                    <th key={h} className="text-left px-3 py-2 text-white/30 uppercase tracking-wide">{h}</th>
                  ))}
                </tr></thead>
                <tbody className="divide-y divide-white/5">
                  {[
                    ['9900 0000 0000 0001', '01/28', '111', '✅ Plată reușită'],
                    ['9900 0000 0000 0002', '01/28', '111', '❌ Fonduri insuficiente'],
                    ['9900 0000 0000 0003', '01/28', '111', '🔐 3DS2 challenge'],
                    ['9900 0000 0000 0004', '01/28', '111', '⏱ Timeout / eroare'],
                  ].map(([nr, exp, cvv, res]) => (
                    <tr key={nr} className="hover:bg-white/3">
                      <td className="px-3 py-2 font-mono text-white">{nr}</td>
                      <td className="px-3 py-2 text-white/60">{exp}</td>
                      <td className="px-3 py-2 text-white/60">{cvv}</td>
                      <td className="px-3 py-2 text-white/70">{res}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button onClick={saveSettings}
              className="inline-flex items-center gap-2 bg-[#295CA8] hover:bg-[#3D72B8] text-white px-6 py-2.5 rounded-lg text-sm font-medium transition-colors">
              {saved ? <><CheckCircle size={16} /> {t('Salvat!', 'Saved!')}</> : t('Salvează configurarea', 'Save configuration')}
            </button>
          </div>
        </div>
      )}

      {/* ── PAYMENTS ── */}
      {section === 'payments' && (
        <div className="space-y-4">
          <div className="text-sm text-white/40">{payments.length} {t('tranzacții înregistrate', 'transactions recorded')}</div>
          {payments.length === 0 ? (
            <div className="py-16 text-center border border-dashed border-white/10 rounded-xl">
              <DollarSign size={32} className="mx-auto mb-3 text-white/20" />
              <p className="text-white/30 text-sm">{t('Nicio tranzacție înregistrată.', 'No transactions recorded yet.')}</p>
              <p className="text-white/20 text-xs mt-1">{t('Tranzacțiile apar automat după procesarea IPN-urilor Netopia.', 'Transactions appear automatically after Netopia IPN processing.')}</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="border-b border-white/10">
                {['ID Comandă', 'NTP ID', 'Client', 'Total', 'Status', t('Data', 'Date')].map(h => (
                  <th key={h} className="text-left px-3 py-2.5 text-xs text-white/30 uppercase tracking-wide">{h}</th>
                ))}
              </tr></thead>
              <tbody className="divide-y divide-white/5">
                {payments.map(p => (
                  <tr key={p.id} className="hover:bg-white/3">
                    <td className="px-3 py-3 font-mono text-[#4ab8ff] text-xs">{p.orderId}</td>
                    <td className="px-3 py-3 font-mono text-white/40 text-xs">{p.ntpId || '—'}</td>
                    <td className="px-3 py-3 text-white">{p.customerName}<div className="text-xs text-white/30">{p.customerEmail}</div></td>
                    <td className="px-3 py-3 font-semibold text-white">{p.amount} {p.currency}</td>
                    <td className="px-3 py-3"><StatusBadge status={p.status} /></td>
                    <td className="px-3 py-3 text-white/40 text-xs">{p.createdAt}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── TEST ── */}
      {section === 'test' && (
        <div className="space-y-5 max-w-lg">
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl px-4 py-3 text-xs text-yellow-300 flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
            {t('Asigură-te că ești în modul Sandbox înainte de a testa. Nu utiliza credențiale reale pentru testare.', 'Make sure you are in Sandbox mode before testing. Do not use real credentials for testing.')}
          </div>

          <div className="bg-[#07090f] border border-white/10 rounded-xl p-5 space-y-4">
            <h3 className="text-sm font-semibold text-white">{t('Inițiază o plată de test', 'Initiate a test payment')}</h3>
            <Field label={`${t('Sumă', 'Amount')} (${settings.currency})`}>
              <Input type="number" value={testAmount} onChange={e => setTestAmount(e.target.value)} placeholder="10.00" />
            </Field>
            <Field label="Email client test">
              <Input type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="test@firma.ro" />
            </Field>
            <button onClick={runTestPayment} disabled={testRunning || !settings.posSignature}
              className="inline-flex items-center gap-2 bg-[#295CA8] hover:bg-[#3D72B8] text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-40 w-full justify-center">
              {testRunning ? <><RefreshCw size={15} className="animate-spin" /> {t('Se procesează...', 'Processing...')}</> : <><Zap size={15} /> {t('Testează plata', 'Test payment')}</>}
            </button>
          </div>

          {testResult && (
            <div className={`rounded-xl border px-5 py-4 space-y-2 ${testResult.ok ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
              <div className={`flex items-center gap-2 font-semibold text-sm ${testResult.ok ? 'text-green-300' : 'text-red-300'}`}>
                {testResult.ok ? <CheckCircle size={16} /> : <XCircle size={16} />}
                {testResult.ok ? t('Inițiere reușită', 'Initiation successful') : t('Eroare', 'Error')}
              </div>
              <p className="text-xs text-white/60">{testResult.message}</p>
              {testResult.url && (
                <a href={testResult.url} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-[#4ab8ff] hover:underline">
                  {t('Deschide pagina de plată →', 'Open payment page →')}
                </a>
              )}
            </div>
          )}

          {/* Integration checklist */}
          <div className="bg-[#07090f] border border-white/10 rounded-xl p-5 space-y-3">
            <h3 className="text-sm font-semibold text-white">{t('Checklist integrare', 'Integration checklist')}</h3>
            {[
              [!!settings.posSignature, 'POS Signature configurat'],
              [!!settings.apiKey, 'API Key configurat'],
              [!!settings.notifyUrl, 'Notify URL (IPN) setat'],
              [!!settings.redirectUrl, 'Redirect URL setat'],
              [!settings.isLive, 'Sandbox activ (pentru testare)'],
              [true, 'netopia-ipn.php uploadat pe server'],
              [true, 'netopia.php uploadat în /cms-api/'],
            ].map(([done, label], i) => (
              <div key={i} className="flex items-center gap-3 text-sm">
                {done ? <CheckCircle size={15} className="text-green-400 flex-shrink-0" /> : <XCircle size={15} className="text-red-400 flex-shrink-0" />}
                <span className={done ? 'text-white/70' : 'text-white/40'}>{label as string}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── EXCHANGE RATE TAB ──────────────────────────────────────────────────────
const LS_EUR_RATE     = 'interpersonal_eur_rate';
const LS_EUR_TS       = 'interpersonal_eur_ts';
const BT_VINDEM_BASE  = 5.2600; // cursul BT VINDEM EUR — actualizat manual dacă BT schimbă
const ONE_HOUR        = 60 * 60 * 1000;

function ExchangeRateTab() {
  const { t } = useLang();

  // markup% salvat în localStorage (min 0 = la paritate BT, implicit 2%)
  const [markup, setMarkup]       = useState<number>(() => {
    const saved = localStorage.getItem('interp_eur_markup');
    return saved ? parseFloat(saved) : 2.00;
  });
  const [markupInput, setMarkupInput] = useState<string>(markup.toFixed(2));
  const [editingMarkup, setEditingMarkup] = useState(false);

  // cursul live BT (citit din cache sau refetched)
  const [btRate, setBtRate]         = useState<number>(BT_VINDEM_BASE);
  const [btTs, setBtTs]             = useState<number>(0);
  const [fetching, setFetching]     = useState(false);
  const [fetchErr, setFetchErr]     = useState('');
  const [btSource, setBtSource]     = useState<'live' | 'cache' | 'static'>('static');

  // rata finală aplicată în coș
  const finalRate = parseFloat((btRate * (1 + markup / 100)).toFixed(4));

  const loadFromCache = useCallback(() => {
    const cached = localStorage.getItem(LS_EUR_RATE);
    const ts     = parseInt(localStorage.getItem(LS_EUR_TS) || '0', 10);
    if (cached && Date.now() - ts < ONE_HOUR) {
      // cursul cache include deja markup-ul — extragem rata BT curată
      const cachedFinal = parseFloat(cached);
      // recalculăm BT din cached / (1 + markup/100)
      setBtRate(parseFloat((cachedFinal / (1 + markup / 100)).toFixed(4)));
      setBtTs(ts);
      setBtSource('cache');
      return true;
    }
    return false;
  }, [markup]);

  const fetchLive = useCallback(async () => {
    setFetching(true);
    setFetchErr('');
    try {
      const res  = await fetch('https://www.bancatransilvania.ro/curs-valutar', {
        signal: AbortSignal.timeout(6000),
      });
      const html = await res.text();
      // Extrage cursul VINDEM EUR din HTML BT
      const match = html.match(/EUR[\s\S]*?(\d+\.\d{4})\s*<\/td>\s*<td[^>]*>\s*(\d+\.\d{4})/);
      let sell = BT_VINDEM_BASE;
      if (match && match[2]) sell = parseFloat(match[2]);

      setBtRate(sell);
      setBtTs(Date.now());
      setBtSource('live');

      // Actualizăm cache-ul în localStorage (rata finală cu markup curent)
      const newFinal = parseFloat((sell * (1 + markup / 100)).toFixed(4));
      localStorage.setItem(LS_EUR_RATE, String(newFinal));
      localStorage.setItem(LS_EUR_TS,   String(Date.now()));
    } catch (e: any) {
      setFetchErr('Nu s-a putut accesa BT. Se folosește rata din cache sau valoarea implicită.');
      setBtRate(BT_VINDEM_BASE);
      setBtSource('static');
    } finally {
      setFetching(false);
    }
  }, [markup]);

  useEffect(() => {
    if (!loadFromCache()) fetchLive();
  }, []);

  const saveMarkup = () => {
    const v = parseFloat(markupInput.replace(',', '.'));
    if (isNaN(v) || v < 0) { setMarkupInput(markup.toFixed(2)); setEditingMarkup(false); return; }
    setMarkup(v);
    localStorage.setItem('interp_eur_markup', String(v));
    // Actualizăm cache-ul cu noua rată finală
    const newFinal = parseFloat((btRate * (1 + v / 100)).toFixed(4));
    localStorage.setItem(LS_EUR_RATE, String(newFinal));
    localStorage.setItem(LS_EUR_TS,   String(Date.now()));
    setEditingMarkup(false);
  };

  const resetMarkup = () => {
    setMarkup(2.00);
    setMarkupInput('2.00');
    localStorage.setItem('interp_eur_markup', '2');
    const newFinal = parseFloat((btRate * 1.02).toFixed(4));
    localStorage.setItem(LS_EUR_RATE, String(newFinal));
    localStorage.setItem(LS_EUR_TS,   String(Date.now()));
    setEditingMarkup(false);
  };

  const cacheAge = btTs ? Math.round((Date.now() - btTs) / 60000) : null;

  return (
    <div className="space-y-6">
      {/* Header card */}
      <div className="bg-white/3 border border-white/10 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-1">
          <TrendingUp size={20} className="text-[#4ab8ff]" />
          <h2 className="text-lg font-bold text-white">Curs Valutar EUR / RON</h2>
        </div>
        <p className="text-sm text-white/40">
          Cursul de referință este <strong className="text-white/60">Banca Transilvania — VINDEM (sell)</strong>.
          Markup-ul procentual se adaugă peste acesta și este aplicat în calculul coșului de cumpărături.
        </p>
      </div>

      {/* Rate display */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* BT VINDEM */}
        <div className="bg-white/3 border border-white/10 rounded-2xl p-5">
          <p className="text-xs text-white/40 uppercase tracking-widest mb-2">BT VINDEM EUR (bază)</p>
          <p className="text-3xl font-bold text-white">{btRate.toFixed(4)}</p>
          <p className="text-xs text-white/30 mt-1">RON / 1 EUR</p>
          <div className="mt-3 flex items-center gap-1.5 text-xs">
            {btSource === 'live'   && <><span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" /><span className="text-green-400">Live BT</span></>}
            {btSource === 'cache'  && <><span className="w-2 h-2 rounded-full bg-yellow-400" /><span className="text-yellow-400">Cache ({cacheAge} min)</span></>}
            {btSource === 'static' && <><span className="w-2 h-2 rounded-full bg-white/30" /><span className="text-white/30">Valoare implicită</span></>}
          </div>
        </div>

        {/* Markup */}
        <div className="bg-white/3 border border-white/10 rounded-2xl p-5">
          <p className="text-xs text-white/40 uppercase tracking-widest mb-2">Markup aplicat</p>
          {editingMarkup ? (
            <div className="flex items-center gap-2">
              <input
                type="number" step="0.01" min="0"
                value={markupInput}
                onChange={e => setMarkupInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && saveMarkup()}
                className="w-24 bg-white/5 border border-[#295CA8] rounded-lg px-3 py-2 text-xl font-bold text-white focus:outline-none"
                autoFocus
              />
              <span className="text-xl font-bold text-white">%</span>
              <button onClick={saveMarkup} className="p-1.5 rounded-lg bg-[#295CA8] text-white hover:bg-[#3D72B8] transition-all"><Save size={14} /></button>
              <button onClick={() => { setMarkupInput(markup.toFixed(2)); setEditingMarkup(false); }} className="p-1.5 rounded-lg bg-white/10 text-white/50 hover:bg-white/20 transition-all"><XCircle size={14} /></button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <p className="text-3xl font-bold text-[#4ab8ff]">{markup.toFixed(2)}%</p>
              <button onClick={() => { setMarkupInput(markup.toFixed(2)); setEditingMarkup(true); }}
                className="p-1.5 rounded-lg bg-white/10 text-white/50 hover:text-white hover:bg-white/20 transition-all">
                <Edit2 size={14} />
              </button>
            </div>
          )}
          <p className="text-xs text-white/30 mt-2">Minim: 0% (la paritate BT)</p>
          {markup < 0.01 && (
            <p className="text-xs text-yellow-400 mt-1 flex items-center gap-1"><AlertTriangle size={11} /> Markup 0% — prețul EUR este exact cursul BT</p>
          )}
        </div>

        {/* Rata finală */}
        <div className="bg-[#295CA8]/15 border border-[#295CA8]/30 rounded-2xl p-5">
          <p className="text-xs text-[#4ab8ff]/70 uppercase tracking-widest mb-2">Rată aplicată în coș</p>
          <p className="text-3xl font-bold text-[#4ab8ff]">{finalRate.toFixed(4)}</p>
          <p className="text-xs text-[#4ab8ff]/50 mt-1">RON / 1 EUR (BT + {markup.toFixed(2)}%)</p>
          <p className="text-xs text-white/40 mt-3">
            Produsele EUR din coș se convertesc la <strong className="text-white/60">{finalRate.toFixed(4)} RON</strong>
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={fetchLive} disabled={fetching}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white/70 hover:text-white hover:bg-white/10 transition-all disabled:opacity-50">
          <RefreshCw size={14} className={fetching ? 'animate-spin' : ''} />
          {fetching ? 'Se actualizează...' : 'Actualizează cursul BT live'}
        </button>
        <button onClick={resetMarkup}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white/50 hover:text-white/80 hover:bg-white/10 transition-all">
          <RefreshCw size={14} /> Reset markup la 2%
        </button>
      </div>

      {/* Error */}
      {fetchErr && (
        <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/20 text-sm text-red-300">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" /> {fetchErr}
        </div>
      )}

      {/* Info box */}
      <div className="bg-white/2 border border-white/8 rounded-2xl p-5 space-y-3">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2"><Info size={15} className="text-[#4ab8ff]" /> Cum funcționează</h3>
        <ul className="space-y-2 text-sm text-white/50">
          <li className="flex items-start gap-2"><CheckCircle size={13} className="text-green-400 mt-0.5 shrink-0" /> Cursul BT VINDEM este citit live de pe <strong className="text-white/60">bancatransilvania.ro/curs-valutar</strong> și memorat 1 oră în browser.</li>
          <li className="flex items-start gap-2"><CheckCircle size={13} className="text-green-400 mt-0.5 shrink-0" /> Markup-ul (<strong className="text-white/60">{markup.toFixed(2)}%</strong>) se aplică peste cursul BT și rezultă rata finală de <strong className="text-white/60">{finalRate.toFixed(4)} RON/EUR</strong>.</li>
          <li className="flex items-start gap-2"><CheckCircle size={13} className="text-green-400 mt-0.5 shrink-0" /> Rata finală este folosită automat în coșul de cumpărături pentru a converti prețurile EUR în RON.</li>
          <li className="flex items-start gap-2"><AlertTriangle size={13} className="text-yellow-400 mt-0.5 shrink-0" /> Markup-ul nu poate merge sub <strong className="text-white/60">0%</strong> (nu poți vinde sub cursul BT VINDEM).</li>
        </ul>
      </div>

      {/* Table: history placeholder */}
      <div className="bg-white/3 border border-white/10 rounded-2xl p-5">
        <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2"><TrendingUp size={15} className="text-[#4ab8ff]" /> Istoric cursuri (ultima actualizare)</h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left">
              <th className="pb-3 text-xs text-white/30 uppercase tracking-wider">Sursă</th>
              <th className="pb-3 text-xs text-white/30 uppercase tracking-wider">BT VINDEM</th>
              <th className="pb-3 text-xs text-white/30 uppercase tracking-wider">Markup</th>
              <th className="pb-3 text-xs text-white/30 uppercase tracking-wider">Rată finală</th>
              <th className="pb-3 text-xs text-white/30 uppercase tracking-wider">Actualizat</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-white/5">
              <td className="py-3 text-white/70">Banca Transilvania</td>
              <td className="py-3 text-white font-medium">{btRate.toFixed(4)} RON</td>
              <td className="py-3 text-[#4ab8ff] font-medium">+{markup.toFixed(2)}%</td>
              <td className="py-3 text-white font-bold">{finalRate.toFixed(4)} RON</td>
              <td className="py-3 text-white/30 text-xs">
                {btTs ? new Date(btTs).toLocaleString('ro-RO') : 'N/A'}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// MAIN FINANCIAL PAGE
// ═══════════════════════════════════════════════════════════════════════════════
export default function FinancialPage() {
  const { t } = useLang();
  const [tab, setTab] = useState<'smartbill' | 'netopia' | 'exchange'>('smartbill');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">{t('Financial', 'Financial')}</h1>
        <p className="text-sm text-white/40 mt-1">{t('Gestionează facturarea SmartBill și procesarea plăților Netopia', 'Manage SmartBill invoicing and Netopia payment processing')}</p>
      </div>

      {/* Main tabs */}
      <div className="flex gap-0 border border-white/10 rounded-xl overflow-hidden w-fit">
        <button onClick={() => setTab('smartbill')}
          className={`flex items-center gap-2.5 px-6 py-3 text-sm font-semibold transition-all border-r border-white/10 ${tab === 'smartbill' ? 'bg-[#295CA8] text-white' : 'bg-white/3 text-white/40 hover:text-white hover:bg-white/5'}`}>
          <FileText size={16} />
          SmartBill
          <span className="text-xs px-2 py-0.5 rounded-full bg-white/10">{t('Facturare', 'Invoicing')}</span>
        </button>
        <button onClick={() => setTab('netopia')}
          className={`flex items-center gap-2.5 px-6 py-3 text-sm font-semibold transition-all border-r border-white/10 ${tab === 'netopia' ? 'bg-[#295CA8] text-white' : 'bg-white/3 text-white/40 hover:text-white hover:bg-white/5'}`}>
          <CreditCard size={16} />
          Netopia
          <span className="text-xs px-2 py-0.5 rounded-full bg-white/10">{t('Plăți online', 'Online payments')}</span>
        </button>
        <button onClick={() => setTab('exchange')}
          className={`flex items-center gap-2.5 px-6 py-3 text-sm font-semibold transition-all ${tab === 'exchange' ? 'bg-[#295CA8] text-white' : 'bg-white/3 text-white/40 hover:text-white hover:bg-white/5'}`}>
          <TrendingUp size={16} />
          {t('Curs Valutar', 'Exchange Rate')}
          <span className="text-xs px-2 py-0.5 rounded-full bg-white/10">EUR/RON</span>
        </button>
      </div>

      {/* Content */}
      {tab === 'smartbill' && <SmartBillTab />}
      {tab === 'netopia'   && <NetopiaTab />}
      {tab === 'exchange'  && <ExchangeRateTab />}
    </div>
  );
}
